import { isNullish } from '@caresend/utils';
import * as turf from '@turf/turf';
import isEqual from 'lodash.isequal';
import mapboxGl, { Marker } from 'mapbox-gl';
import { ref } from 'vue';
import { isPoint, isPolygonOrMultiPolygon, maybeGetSourceGroupForProcessing, createBlueCircleElement, createImageElement, isCoordinates, roundedLatLng } from './private/index.js';
import '@caresend/types';
import '@sentry/vue';
import { toastErrorAndReport } from '../../../../functions/toast/toast.js';
import { getSeededColor } from '../../../../functions/color.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import { getStore } from '../../../../store/helpers/initStore.js';

const markers = ref({});
const markersByPointID = ref({});
/**
 * If the map is rendered on the portal and there is enough space
 * to display the user location centered on the map, we want to measure
 * the width of the portal contents and apply that offset to the user's
 * latitude so that the user is centered on the map.
 */
const getCenterOffsetLat = (zoom) => {
    const portalRouterContainers = document
        .getElementsByClassName('Portal__panel-inner');
    if (portalRouterContainers.length === 0)
        return 0;
    const portalRouterContainer = portalRouterContainers[0];
    if (!portalRouterContainer)
        return 0;
    const portalRouterContainerWidth = portalRouterContainer.clientWidth;
    const windowWidth = window.innerWidth;
    const remainder = windowWidth - portalRouterContainerWidth + 78;
    if (remainder < 200)
        return 0;
    /**
     * At zoom level 0, the world is 512 pixels wide
     * Each zoom level doubles the number of pixels
     */
    const pixelsPerLongitude = 512 * (2 ** zoom);
    const degreesPerPixel = 360 / pixelsPerLongitude;
    /**
     * Divide by 2 to move the center by 1/2 of the remaining space
     */
    return (remainder / 2) * degreesPerPixel;
};
const getLngLatFromLngLatLike = (lngLat) => {
    if (Array.isArray(lngLat))
        return lngLat;
    if ('lng' in lngLat)
        return [lngLat.lng, lngLat.lat];
    return [lngLat.lon, lngLat.lat];
};
const displayLabelOnPins = ({ buildMarker, emit, id, sourceGroup, map, }) => {
    var _a;
    if (!map)
        return;
    const sourceGroupData = sourceGroup.map((src) => src.data).flat();
    const markerElement = buildMarker(sourceGroupData, map);
    const firstData = sourceGroupData[0];
    if (firstData && isPoint(firstData.geometry)) {
        const { geometry, properties } = firstData;
        const { coordinates } = geometry;
        if (isCoordinates(coordinates)) {
            const marker = new Marker(markerElement !== null && markerElement !== void 0 ? markerElement : document.createElement('div'), { anchor: 'bottom' })
                .setLngLat(coordinates)
                .addTo(map);
            if ((_a = properties.marker) === null || _a === void 0 ? void 0 : _a.isClickable) {
                const markerID = properties.id;
                if (markerID) {
                    marker.getElement().addEventListener('click', () => {
                        emit('markerClick', markerID);
                    });
                }
                else {
                    toastErrorAndReport('Missing ID for clicked map marker');
                }
            }
            markers.value[id] = {
                id,
                latlng: coordinates.join(','),
                marker,
                children: sourceGroupData.map((e) => { var _a, _b; return (_b = (_a = e.properties) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''; }),
            };
            sourceGroupData.forEach((e) => {
                var _a;
                if (!((_a = e.properties) === null || _a === void 0 ? void 0 : _a.id))
                    return;
                markersByPointID.value[e.properties.id] = id;
            });
        }
    }
};
const fitToBounds = ({ map, sources, paddingBottom, customPadding, displayUser, userLngLat, maxZoomInitialTransition, }) => {
    if (!map)
        return;
    let paddingBottomVal = 50;
    if (paddingBottom) {
        paddingBottomVal += paddingBottom;
    }
    const mapBounds = new mapboxGl.LngLatBounds();
    if (displayUser && userLngLat) {
        mapBounds.extend(userLngLat);
    }
    sources.map(({ data }) => {
        data.forEach((e) => {
            const { geometry } = e;
            if (isPoint(geometry)) {
                const coordinates = {
                    lng: geometry.coordinates[0] || 0,
                    lat: geometry.coordinates[1] || 0,
                };
                mapBounds.extend(coordinates);
            }
            const isBbox2D = (bbox) => bbox.length === 4;
            if (isPolygonOrMultiPolygon(geometry)) {
                const bbox = turf.bbox(geometry);
                if (isBbox2D(bbox)) {
                    mapBounds.extend(bbox);
                }
            }
        });
    });
    const padding = customPadding || { bottom: paddingBottomVal, top: 80, left: 30, right: 30 };
    if (Object.keys(mapBounds).length === 0)
        return;
    // TODO: Adjusted to apply only to single-point maps that display the user,
    // because single-point map centering was broken in CARE-4521. Based on the
    // comment on `getCenterOffsetLat`, further work will be needed here to
    // achieve the desired behavior.
    const { _ne, _sw } = mapBounds;
    const isSingleLocation = isEqual(_ne, _sw);
    if (isSingleLocation && displayUser && userLngLat) {
        const [lng, lat] = getLngLatFromLngLatLike(userLngLat);
        if (isNullish(lat) || isNullish(lng))
            return;
        const offsetLat = getCenterOffsetLat(map.getZoom());
        map.panTo({
            lng: lng - offsetLat,
            lat: lat,
        }, {
            animate: true,
        });
        return;
    }
    try {
        map.fitBounds(mapBounds, {
            padding,
            linear: false,
            maxZoom: maxZoomInitialTransition,
        });
    }
    catch (error) {
        console.error(error);
    }
};
const sourcesPreprocessor = (sources, groupingThreshold) => {
    const polygons = [];
    const pointsByLatLng = {};
    const GROUPING_THRESHOLD = groupingThreshold;
    sources.forEach((s) => {
        var _a, _b, _c, _d, _e;
        const source = { ...s };
        if (isPolygonOrMultiPolygon((_a = source.data[0]) === null || _a === void 0 ? void 0 : _a.geometry)) {
            polygons.push(source);
            return;
        }
        if (!isPoint((_b = source.data[0]) === null || _b === void 0 ? void 0 : _b.geometry))
            return;
        const [lat, lng] = (_d = (_c = source.data[0]) === null || _c === void 0 ? void 0 : _c.geometry.coordinates) !== null && _d !== void 0 ? _d : [0, 0];
        const [roundedLat, roundedLng] = roundedLatLng(lat, lng, GROUPING_THRESHOLD);
        if (!pointsByLatLng[roundedLat])
            pointsByLatLng[roundedLat] = {};
        pointsByLatLng[roundedLat][roundedLng] = [
            ...((_e = pointsByLatLng[roundedLat][roundedLng]) !== null && _e !== void 0 ? _e : []),
            source,
        ].sort((a, b) => a.id.localeCompare(b.id));
    });
    return { pointsByLatLng, polygons };
};
/**
 * For contexts where we want to disable grouping, and have each source
 * group contain one source. TODO: Refactor to instead group only when needed,
 * rather than grouping and then ungrouping.
 */
const ungroupSourceGroups = (sourceGroups) => {
    const flattened = sourceGroups.flat();
    return flattened.map((source) => [source]);
};
const processSources = ({ sources: inputSources, allSources, buildMarker, disableGrouping, displayPopUp = false, emit, map, groupingThreshold, }) => {
    const promises = [];
    const sources = {};
    /**
     * Handle point updates.
     * All sources are provided upon update and removal of sources
     * This allows us to determine exactly which source groups to reprocess
     * in the event that a source is updated or removed but a group sibling is not.
     */
    if (allSources) {
        const { pointsByLatLng: allPoints } = sourcesPreprocessor(allSources, groupingThreshold);
        const oldSourceGroups = inputSources
            .map((source) => maybeGetSourceGroupForProcessing(source, allPoints, groupingThreshold))
            .flat();
        oldSourceGroups.forEach((source) => {
            sources[source.id] = source;
        });
    }
    const sourcesToProcess = !allSources ? inputSources : Object.values(sources);
    const { pointsByLatLng } = sourcesPreprocessor(sourcesToProcess, groupingThreshold);
    const { polygons } = sourcesPreprocessor(inputSources, groupingThreshold);
    polygons.forEach(({ data, id }) => {
        var _a;
        if (!map)
            return;
        if (isPolygonOrMultiPolygon((_a = data[0]) === null || _a === void 0 ? void 0 : _a.geometry)) {
            if (!map.getSource(id)) {
                map.addSource(id, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: data,
                    },
                });
            }
            if (map.getLayer(id)) {
                map.removeLayer(id);
            }
            map.addLayer({
                id,
                type: 'fill',
                source: id,
                paint: {
                    'fill-color': getSeededColor(id),
                    'fill-opacity': 0.55,
                },
            });
        }
    });
    const groupedSourceGroups = Object.values(pointsByLatLng).map((lngGroup) => Object.values(lngGroup)).flat();
    const sourceGroups = disableGrouping
        ? ungroupSourceGroups(groupedSourceGroups)
        : groupedSourceGroups;
    sourceGroups.map((mapSourceGroup) => new Promise((resolve) => {
        var _a, _b;
        if (!map || !mapSourceGroup[0])
            return resolve(false);
        const { id, data } = mapSourceGroup[0];
        if (!id || !data)
            return resolve(false);
        const source = map.getSource(id);
        if (source) {
            source.setData({
                type: 'FeatureCollection',
                features: data,
            });
            displayLabelOnPins({
                buildMarker,
                emit,
                id,
                map,
                sourceGroup: mapSourceGroup,
            });
            return resolve(true);
        }
        if (!isPoint((_a = data[0]) === null || _a === void 0 ? void 0 : _a.geometry)
            && !isPolygonOrMultiPolygon((_b = data[0]) === null || _b === void 0 ? void 0 : _b.geometry)) {
            return;
        }
        map.addSource(id, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: data,
            },
        });
        if (displayPopUp) {
            displayLabelOnPins({
                buildMarker,
                emit,
                id,
                sourceGroup: mapSourceGroup,
                map,
            });
        }
        resolve(true);
    }));
    return Promise.all(promises);
};
const removeSources = ({ sources: inputSources, allSources, map, groupingThreshold, }) => {
    /** Handle point updates. Use all points to determine which sources groups to remove. */
    const { pointsByLatLng: allPoints } = sourcesPreprocessor(allSources, groupingThreshold);
    const sourceGroups = inputSources.map((source) => maybeGetSourceGroupForProcessing(source, allPoints, groupingThreshold)).flat();
    const sources = {};
    sourceGroups.forEach((source) => {
        sources[source.id] = source;
    });
    const { polygons: removedPolygons } = sourcesPreprocessor(inputSources, groupingThreshold);
    removedPolygons.forEach((source) => {
        sources[source.id] = source;
    });
    Object.values(sources).forEach(({ id }) => {
        const existingMarkerID = markersByPointID.value[id];
        const marker = markers.value[existingMarkerID !== null && existingMarkerID !== void 0 ? existingMarkerID : id];
        const markerID = existingMarkerID;
        try {
            if (markerID && map.getLayer(markerID)) {
                map.removeLayer(markerID);
            }
            if (markerID && map.getSource(markerID)) {
                map.removeSource(markerID);
            }
            if (map.getLayer(id)) {
                map.removeLayer(id);
            }
            if (map.getSource(id)) {
                map.removeSource(id);
            }
        }
        catch (error) {
            console.error(error);
        }
        if (marker) {
            marker.marker.remove();
            delete markers.value[id];
        }
    });
};
const addUserMapMarker = (map, userLngLat) => {
    var _a;
    if (!map || !userLngLat)
        return;
    const store = getStore();
    const info = store.getters['auth/getUserInfo'];
    const element = (_a = createBlueCircleElement()) !== null && _a !== void 0 ? _a : createImageElement(info === null || info === void 0 ? void 0 : info.picture);
    new Marker(element, { anchor: 'center' })
        .setLngLat(userLngLat)
        .addTo(map);
};
const defaultCenterAndZoom = {
    coords: [-98.5795, 39.8283],
    zoom: 3,
};
let lastLocation = {
    lat: 0,
    lng: 0,
    timestamp: 0,
};
const getCurrentPosition = () => new Promise((resolve, reject) => {
    if (Date.now() - lastLocation.timestamp < 1000 * 60 * 1) {
        resolve({
            lat: lastLocation.lat,
            lng: lastLocation.lng,
        });
    }
    navigator.geolocation.getCurrentPosition(({ coords }) => {
        lastLocation = {
            lat: coords.latitude,
            lng: coords.longitude,
            timestamp: Date.now(),
        };
        resolve({
            lng: coords.longitude, lat: coords.latitude,
        });
    }, reject);
});
const getCenterAndZoom = async (getUserLocation) => {
    if (!getUserLocation)
        return defaultCenterAndZoom;
    try {
        const { lat, lng } = await getCurrentPosition();
        return {
            coords: [lng, lat],
            zoom: 10,
        };
    }
    catch (error) {
        return defaultCenterAndZoom;
    }
};

export { addUserMapMarker, displayLabelOnPins, fitToBounds, getCenterAndZoom, getCurrentPosition, processSources, removeSources };
